import Cookies from "js-cookie";
import { isOrganicAccess } from "utils/isOrganicAccess";
import {
  getAllPersistedClickIds,
  getPersistedClickRefs,
  readFbCookies,
  startsNewTouch
} from "utils/clickIds";
import { getUserAgentData } from "utils/userAgentData";

export { startsNewTouch };

/**
 * The saved touch for the spocket_utm cookie: the landing query, plus when the
 * visitor arrived and on which page, when the link didn't already carry them
 * (links from the website do). Only for a new ad or partner touch.
 */
export const withTouchContext = search => {
  try {
    if (!startsNewTouch(search)) return search;

    const params = new URLSearchParams(search);
    const extra = [];
    if (!params.get("touch_ts")) extra.push(`touch_ts=${Math.floor(Date.now() / 1000)}`);
    if (!params.get("landing_url")) {
      extra.push(`landing_url=${encodeURIComponent(`${window.location.origin}${window.location.pathname}`)}`);
    }
    if (!extra.length) return search;
    return search ? `${search}&${extra.join("&")}` : `?${extra.join("&")}`;
  } catch (_) {
    return search;
  }
};

/**
 * The attribution payload for POST /stores/utm. Sign-up checkout and in-app
 * subscribe send the same payload as `utm`, so the Stripe subscription they
 * create carries it in its metadata.
 *
 * Never throws: attribution is optional and must not break a checkout.
 *
 * @returns {Object|null} null when there is nothing to attribute
 */
export function buildUtmPayload() {
  try {
    return buildPayload();
  } catch (_) {
    return null;
  }
}

function buildPayload() {
  let spocketUtm = Cookies.get("spocket_utm");
  const isOrganic = isOrganicAccess();
  const clickIds = getAllPersistedClickIds();

  if (!spocketUtm && isOrganic) {
    spocketUtm = "?utm_source=organic";
  }

  // we want to add other cookies to utm payload
  // so far, we want to add the ps_xid cookie
  // but we can add more if needed
  let updatedUtm = spocketUtm || "?";
  const appendParam = (utm, name, value) => {
    const queryStart = utm.indexOf("?");
    const hasExistingParams = queryStart !== -1 && utm.length > queryStart + 1;
    const separator = hasExistingParams ? "&" : queryStart === -1 ? "?" : "";
    return `${utm}${separator}${name}=${value}`;
  };

  const otherCookies = [
    "ps_xid", // partner stack
    "sscid", // share a sale
    "irclickid" // impact radius
  ];
  otherCookies.forEach(name => {
    const value = Cookies.get(name);
    if (value) {
      updatedUtm = appendParam(updatedUtm, name, encodeURIComponent(value));
    }
  });

  Object.entries({ ...clickIds, ...getPersistedClickRefs() }).forEach(([name, value]) => {
    if (value && !updatedUtm.includes(`${name}=`)) {
      updatedUtm = appendParam(updatedUtm, name, encodeURIComponent(value));
    }
  });

  // Append journey information if available
  try {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const journeyFromQuery = urlSearchParams.get("journey");
    const journeyFromStorage =
      typeof localStorage !== "undefined" ? localStorage.getItem("landing_page_signup") : null;
    const journey = journeyFromQuery || journeyFromStorage;
    if (journey) {
      updatedUtm = appendParam(updatedUtm, "journey", encodeURIComponent(journey));
    }
  } catch (_) {
    // no-op if window or URLSearchParams is not available
  }

  const payload = withFallbackAttribution(withoutQaValues(formatUtm(updatedUtm)));
  if (!Object.keys(payload).length) return null;

  // Browser and device context, added after the fallback: a visitor having a Meta
  // cookie or a browser says nothing about where they came from.
  return { ...payload, ...readFbCookies(), ...getUserAgentData() };
}

// Test values QA uses on live ads. Kept where QA checks them: staging and local builds.
const QA_VALUES = {
  utm_campaign: ["qa111", "qb111", "qb444"],
  campaignid: ["qa111", "qb111", "qb444"],
  gclid: ["qa_gclid_test", "test1"],
  msclkid: ["qb111", "qb444"],
  utm_source: ["test"]
};

const keepsQaValues = () => {
  try {
    const { hostname } = window.location;
    return hostname.includes("staging-spocket.co") || hostname === "localhost" || hostname === "127.0.0.1";
  } catch (_) {
    return false;
  }
};

export const withoutQaValues = (payload, keepQa = keepsQaValues()) => {
  if (keepQa) return payload;

  const result = { ...payload };
  Object.entries(QA_VALUES).forEach(([key, values]) => {
    if (values.includes(String(result[key] || "").toLowerCase())) delete result[key];
  });
  return result;
};

// Utils
// UTM keys come from a user-controlled cookie/query string. To prevent remote
// property injection (and prototype pollution), we only forward keys that appear
// in this allowlist, and we index using the constant from the allowlist rather
// than the user-supplied string so the written property name is never tainted.
const ALLOWED_UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_id",
  "ps_xid", // partner stack
  "sscid", // share a sale
  "irclickid", // impact radius
  "journey",
  "gclid", // google click id
  "gbraid", // google app click id
  "wbraid", // google web-to-app click id
  "fbclid", // facebook click id
  "msclkid", // microsoft click id
  "oppref", // openai click reference
  "touch_ts", // when the ad was clicked (unix seconds)
  "landing_url", // first page of the visit
  // ad-network keys POST /stores/utm maps onto the store UTM
  "campaignid",
  "adgroupid",
  "utm_adset",
  "utm_adgroup",
  "utm_adid",
  "utm_matchtype",
  "k",
  "d",
  "utm_device",
  "utm_keywordid",
  "utm_loc_physical",
  "utm_loc_interest",
  "utm_network",
  "utm_placement",
  "utm_assetgroup",
  "utm_sitelink",
  "utm_medium_variant"
];

const MAX_VALUE_LENGTH = 500;

// Values arrive URL-encoded ("a+b", "a%20b") and the API stores them as sent,
// so they are decoded here, once, and capped. journey is left as it is: the API
// decodes and parses it itself.
const formatValue = (key, value) => {
  if (key === "journey") return value;
  let decoded = value;
  try {
    decoded = decodeURIComponent(value.replace(/\+/g, " "));
  } catch (_) {
    // malformed encoding: keep the raw value
  }
  // Cut by character, not UTF-16 unit, so an emoji is never split in half.
  return Array.from(decoded)
    .slice(0, MAX_VALUE_LENGTH)
    .join("");
};

const formatUtm = utms => {
  const result = Object.create(null);
  const query = typeof utms === "string" ? utms.split("?")[1] : "";
  if (!query) {
    return {};
  }
  query.split("&").forEach(utm => {
    const [key, ...rest] = utm.split("=");
    const allowedKey = ALLOWED_UTM_KEYS.find(allowed => allowed === key);
    if (allowedKey) {
      result[allowedKey] = formatValue(allowedKey, rest.join("="));
    }
  });

  return Object.assign({}, result);
};

const SPOCKET_HOSTNAME = /(^|\.)(staging-)?spocket\.co$/;
const APP_HOSTNAME = /^(app|alpha)\.(staging-)?spocket\.co$|^localhost$|^127\.0\.0\.1$/;

// Hostname of the first page that sent the visitor to the app (stored by
// public/index.html). "" when the browser sent no referrer at all.
const landingReferrerHostname = () => {
  try {
    const referrer = localStorage.getItem("referrer");
    return referrer ? new URL(referrer).hostname.toLowerCase() : "";
  } catch (_) {
    return null;
  }
};

// "www.youtube.com" → "youtube", "bbc.co.uk" → "bbc"
const sourceLabel = hostname => {
  const parts = hostname.split(".");
  if (parts.length >= 3 && ["co", "com", "org", "net"].includes(parts[parts.length - 2])) {
    return parts[parts.length - 3];
  }
  return parts.length >= 2 ? parts[parts.length - 2] : parts[0];
};

/**
 * Labels a visit that arrived without a utm_source, so reporting can tell
 * genuinely direct traffic apart from referrals and from attribution we lost:
 *  - Google click ID only → google / cpc
 *  - Microsoft click ID only → bing / cpc
 *  - OpenAI click reference only → openai / paid_chat
 *  - another site → <site> / referral
 *  - one of our own sites (blog, help centre) → no source / internal
 *  - nothing at all → direct / none
 * utm_sitesourcename keeps the referring hostname.
 *
 * utm_source is never set for affiliate visits: PartnerStack reconciliation
 * treats a store with a non-affiliate utm_source as non-affiliate.
 */
export const withFallbackAttribution = payload => {
  if (payload.utm_source) return payload;

  const result = { ...payload };
  const isAffiliate = !!(payload.ps_xid || payload.sscid || payload.irclickid);
  const setSource = source => {
    if (!isAffiliate) result.utm_source = source;
  };

  if (payload.gclid || payload.gbraid || payload.wbraid) {
    setSource("google");
    result.utm_medium = result.utm_medium || "cpc";
    return result;
  }

  if (payload.msclkid) {
    setSource("bing");
    result.utm_medium = result.utm_medium || "cpc";
    return result;
  }

  if (payload.oppref) {
    setSource("openai");
    result.utm_medium = result.utm_medium || "paid_chat";
    return result;
  }

  const hostname = landingReferrerHostname();
  if (hostname === null || APP_HOSTNAME.test(hostname)) return result;

  if (!hostname) {
    const hasAttribution = Object.keys(payload).some(key => key !== "journey");
    if (!hasAttribution) {
      result.utm_source = "direct";
      result.utm_medium = "none";
    }
    return result;
  }

  result.utm_sitesourcename = hostname.replace(/^www\./, "");
  if (SPOCKET_HOSTNAME.test(hostname)) {
    result.utm_medium = result.utm_medium || "internal";
  } else {
    setSource(sourceLabel(result.utm_sitesourcename));
    result.utm_medium = result.utm_medium || "referral";
  }
  return result;
};
